import React, { useState, useEffect } from 'react';
import { Layout, Avatar, Dropdown, Typography, Button, Space, Tooltip, Badge } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined, LogoutOutlined, UserOutlined, GlobalOutlined, TeamOutlined,
  LeftOutlined, RightOutlined, UsergroupAddOutlined, QuestionCircleOutlined, WalletOutlined, GiftOutlined,
  InboxOutlined, AccountBookOutlined, CrownOutlined, BellOutlined, BankOutlined, MedicineBoxOutlined,
  ContactsOutlined, DatabaseOutlined, FileTextOutlined,
} from '@ant-design/icons';
import { getNotificationsApi } from '../../api/notifications';
import iconLogo         from '../../icons/logo.png';
import logoTitle        from '../../icons/logo-title.png';
import iconDashboard    from '../../icons/dashboard.png';
import iconReport       from '../../icons/report.png';
import iconFarm         from '../../icons/farm.png';
import iconCrop         from '../../icons/crop.png';
import iconPlot         from '../../icons/plot.png';
import iconAccount      from '../../icons/account.png';
import iconSettings     from '../../icons/settings.png';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { toggleSidebar, setLanguage } from '../../store/slices/uiSlice';
import { useTranslation } from 'react-i18next';
import { USER_IMAGE } from '../../utils/constants';
import { SUPPORTED_LANGS } from '../../utils/constants';

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

function NavIcon({ src }) {
  return (
    <div style={{
      width: 18, height: 18,
      backgroundColor: 'currentColor',
      WebkitMask: `url(${src}) no-repeat center / contain`,
      mask: `url(${src}) no-repeat center / contain`,
      flexShrink: 0,
    }} />
  );
}

const NAV_ITEMS = [
  { key: '/dashboard',     icon: <NavIcon src={iconDashboard} />,     labelKey: 'dashboard'     },
  { key: '/farms',         icon: <NavIcon src={iconFarm} />,          labelKey: 'my_farms'      },
  { key: '/plots',         icon: <NavIcon src={iconPlot} />,          labelKey: 'plots'         },
  { key: '/crops',         icon: <NavIcon src={iconCrop} />,          labelKey: 'crops'         },
  { key: '/reports',       icon: <NavIcon src={iconReport} />,        labelKey: 'reports'       },
  { key: '/accounts',      icon: <NavIcon src={iconAccount} />,       labelKey: 'accounts'      },
  { key: '/bank-accounts', icon: <BankOutlined />,                    label: 'Bank Accounts'    },
  { key: '/stock',         icon: <InboxOutlined />,                   labelKey: 'stock'         },
  { key: '/daily-expense', icon: <AccountBookOutlined />,             label: 'Daily Expense'    },
  { key: '/user-access',   icon: <TeamOutlined />,                    labelKey: 'user_access', ownerOnly: true },
  { key: '/refer',         icon: <GiftOutlined />,                    label: 'Rewards & Earn'   },
  { key: '/settings',      icon: <NavIcon src={iconSettings} />,      labelKey: 'settings'      },
];

const ADMIN_NAV_ITEMS = [
  { key: '/admin/users',          icon: <UsergroupAddOutlined />,   label: 'Users' },
  { key: '/admin/subscriptions',  icon: <CrownOutlined />,          label: 'Subscriptions' },
  { key: '/admin/promoters',      icon: <TeamOutlined />,           label: 'Promoters' },
  { key: '/admin/redeem-balance', icon: <WalletOutlined />,         label: 'Redeem Requests' },
  { key: '/admin/help',           icon: <QuestionCircleOutlined />,  label: 'Help & Support' },
  { key: '/admin/notifications',  icon: <BellOutlined />,            label: 'Notifications' },
  { key: '/admin/crop-diseases', icon: <MedicineBoxOutlined />,     label: 'Crop Diseases' },
  { key: '/admin/crm',           icon: <ContactsOutlined />,        label: 'CRM'           },
  { key: '/admin/crop-data',     icon: <DatabaseOutlined />,        label: 'Crop Data'     },
  { key: '/admin/crop-raw',      icon: <FileTextOutlined />,        label: 'Raw Data'      },
];

const SIDEBAR_BG   = '#1a6b2a';
const ACTIVE_BG    = 'rgba(255,255,255,0.16)';
const HOVER_BG     = 'rgba(255,255,255,0.09)';
const ACTIVE_COLOR = '#ffffff';
const MUTED_COLOR  = 'rgba(255,255,255,0.68)';

export default function AppLayout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch();
  const { t, i18n } = useTranslation();
  const { sidebarCollapsed } = useSelector((s) => s.ui);
  const { user }             = useSelector((s) => s.auth);
  const [hoveredKey, setHoveredKey] = useState(null);

  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    getNotificationsApi({ lang: i18n.language, user_id: userId })
      .then((res) => {
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setNotifCount(list.filter((n) => !n.seen).length);
      })
      .catch(() => {});
  }, [user?.id, i18n.language]);

  const canManageUsers  = user?.role === 'super_admin' || user?.role === 'manager';
  const isAdmin         = user?.id === 4826;
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.ownerOnly || canManageUsers);
  const allNavItems     = isAdmin ? [...visibleNavItems, ...ADMIN_NAV_ITEMS] : visibleNavItems;
  const selectedKey     = allNavItems.find((item) => location.pathname.startsWith(item.key))?.key || '/dashboard';

  const userMenu = {
    items: [
      { key: 'profile', label: t('profile'), icon: <UserOutlined /> },
      { key: 'logout',  label: t('logout'),  icon: <LogoutOutlined />, danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') { dispatch(logout()); navigate('/login'); }
      else if (key === 'profile') navigate('/settings');
    },
  };

  const langMenu = {
    items: SUPPORTED_LANGS.map((l) => ({ key: l.code, label: l.label })),
    onClick: ({ key }) => { dispatch(setLanguage(key)); i18n.changeLanguage(key); },
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsed={sidebarCollapsed}
        trigger={null}
        style={{
          background: SIDEBAR_BG,
          height: '100vh',
          position: 'sticky',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '2px 0 12px rgba(0,0,0,0.18)',
        }}
        width={220}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* ── Brand ── */}
          <div style={{
            padding: sidebarCollapsed ? '14px 0' : '10px 16px',
            display: 'flex', alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            background: '#fff',
            borderBottom: '1px solid #e8e8e8',
            flexShrink: 0,
            minHeight: 60,
          }}>
            {sidebarCollapsed ? (
              <img src={iconLogo} alt="logo" style={{ width: 32, height: 32, display: 'block', objectFit: 'contain' }} />
            ) : (
              <img src={logoTitle} alt="Kheti Hisaab" style={{ height: 40, width: 'auto', display: 'block' }} />
            )}
          </div>

          {/* ── Nav items ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0', overflowX: 'hidden' }}>
            {allNavItems.map((item, idx) => {
              const isActive  = selectedKey === item.key;
              const isHovered = hoveredKey === item.key;
              const isFirstAdmin = isAdmin && idx === visibleNavItems.length;

              const icon = item.icon;

              const label = item.labelKey ? t(item.labelKey) : item.label;

              const navRow = (
                <div
                  onClick={() => navigate(item.key)}
                  onMouseEnter={() => setHoveredKey(item.key)}
                  onMouseLeave={() => setHoveredKey(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: sidebarCollapsed ? '11px 0' : '11px 18px',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    cursor: 'pointer',
                    margin: sidebarCollapsed ? '2px 0' : '2px 10px 2px 0',
                    borderRadius: sidebarCollapsed ? 0 : '0 12px 12px 0',
                    background: isActive ? ACTIVE_BG : isHovered ? HOVER_BG : 'transparent',
                    borderLeft: isActive
                      ? '3px solid rgba(255,255,255,0.90)'
                      : '3px solid transparent',
                    color: isActive ? ACTIVE_COLOR : MUTED_COLOR,
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 13.5,
                    transition: 'background 0.15s, color 0.15s',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ fontSize: 17, lineHeight: 1, display: 'flex', flexShrink: 0 }}>{icon}</span>
                  {!sidebarCollapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>}
                </div>
              );

              const row = (
                <React.Fragment key={item.key}>
                  {isFirstAdmin && (
                    <div style={{ margin: '8px 10px 4px', borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 8 }}>
                      {!sidebarCollapsed && (
                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', paddingLeft: 8 }}>
                          Admin
                        </div>
                      )}
                    </div>
                  )}
                  {sidebarCollapsed
                    ? <Tooltip title={label} placement="right">{navRow}</Tooltip>
                    : navRow
                  }
                </React.Fragment>
              );

              return row;
            })}
          </div>

          {/* ── Collapse toggle ── */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.10)', flexShrink: 0 }}>
            <div
              onClick={() => dispatch(toggleSidebar())}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'flex-end',
                padding: '8px 16px', cursor: 'pointer', color: MUTED_COLOR,
                fontSize: 12, gap: 6,
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#fff'}
              onMouseLeave={e => e.currentTarget.style.color = MUTED_COLOR}
            >
              {sidebarCollapsed
                ? <RightOutlined style={{ fontSize: 11 }} />
                : <><span style={{ fontSize: 11 }}>Collapse</span><LeftOutlined style={{ fontSize: 11 }} /></>
              }
            </div>
          </div>

          {/* ── User profile ── */}
          <Dropdown menu={userMenu} placement="topRight" trigger={['click']}>
            <div
              style={{
                padding: sidebarCollapsed ? '12px 0' : '12px 16px',
                display: 'flex', alignItems: 'center', gap: 10,
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                cursor: 'pointer',
                borderTop: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(0,0,0,0.12)',
                flexShrink: 0,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.22)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.12)'}
            >
              <Avatar
                src={user?.photo_url ? USER_IMAGE(user.photo_url) : null}
                icon={!user?.photo_url && <UserOutlined />}
                size={32}
                style={{ background: 'rgba(255,255,255,0.22)', flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.30)' }}
              />
              {!sidebarCollapsed && (
                <div style={{ overflow: 'hidden', flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user?.name}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.50)', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user?.email || user?.role || ''}
                  </div>
                </div>
              )}
            </div>
          </Dropdown>

        </div>
      </Sider>

      <Layout>
        <Header style={{
          background: '#fff', padding: '0 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          position: 'sticky', top: 0, zIndex: 1001,
        }}>
          <Button
            type="text" size="large"
            icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => dispatch(toggleSidebar())}
          />
          <Space>
            <Dropdown menu={langMenu} placement="bottomRight">
              <Button type="text" icon={<GlobalOutlined />} />
            </Dropdown>
            <Tooltip title="Notifications">
              <Badge count={notifCount} size="small" offset={[-2, 2]}>
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  onClick={() => { setNotifCount(0); navigate('/notifications'); }}
                />
              </Badge>
            </Tooltip>
            <Dropdown menu={userMenu} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  src={user?.photo_url ? USER_IMAGE(user.photo_url) : null}
                  icon={!user?.photo_url && <UserOutlined />}
                  style={{ background: '#1a6b2a' }}
                />
                <Text strong style={{ display: window.innerWidth > 768 ? 'inline' : 'none' }}>
                  {user?.name}
                </Text>
                {user?.id && (
                  <Text type="secondary" style={{ fontSize: 11, display: window.innerWidth > 768 ? 'inline' : 'none' }}>
                    #{user.id}
                  </Text>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ padding: 24, minHeight: 'calc(100vh - 64px)' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
